import React from "react"
import { Card, Accordion } from "react-bootstrap"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { usePartner } from "@src/partner"
import { Page } from "@src/components"
import { colors } from "@src/constants"
import { useFleets } from "@src/settings/fleets"
import { getFleetsUuidFences, deleteFleetsUuidFenceId } from "@src/api"
import { FENCE } from "@src/settings/geofence/types"
import FleetAccordionItem from "./components/FleetAccordionItem"
import "./SelectFleetScreen.css"

const SelectFleetScreen: React.FC = () => {
  const { partner } = usePartner()
  const { t } = useTranslation()
  const { fleets } = useFleets()

  const styles = { warningCard: { backgroundColor: colors.whiteGray } }

  const [expandedFleetUuid, setExpandedFleetUuid] = React.useState<string | null>(null)
  const [fencesByFleet, setFencesByFleet] = React.useState<
    Record<string, { loading: boolean; items: FENCE[] }>
  >({})

  const handleAccordionSelect = async (eventKey: string | string[] | null) => {
    const fleetUuid = Array.isArray(eventKey) ? eventKey[0] : eventKey
    setExpandedFleetUuid(fleetUuid)
    if (!fleetUuid) return
    if (!fencesByFleet[fleetUuid]) {
      setFencesByFleet((prev) => ({ ...prev, [fleetUuid]: { loading: true, items: [] } }))
      const { response, data } = await getFleetsUuidFences(partner.uuid, fleetUuid)
      setFencesByFleet((prev) => ({
        ...prev,
        [fleetUuid]: { loading: false, items: response.ok ? data.fences || [] : [] },
      }))
    }
  }

  const handleDeleteFence = async (fleetUuid: string, fenceId: number) => {
    const confirmDelete = window.confirm(t("settings.fence.delete_fence_msg"))
    if (!confirmDelete) return
    const { response } = await deleteFleetsUuidFenceId(partner.uuid, fleetUuid, fenceId)
    if (response.ok) {
      const { response: r, data } = await getFleetsUuidFences(partner.uuid, fleetUuid)
      setFencesByFleet((prev) => ({
        ...prev,
        [fleetUuid]: {
          loading: false,
          items: r.ok ? data.fences || [] : prev[fleetUuid]?.items || [],
        },
      }))
    }
  }

  return (
    <Page isSettings showHeader headerValue={t("settings.fleets.geofence")}>
      {fleets && fleets.length === 0 ? (
        <Card style={styles.warningCard} className="mt-4 p-2">
          <span>{t("settings.fence.no_fleets_cta")} </span>
          <Link to={`/${partner?.uuid}/settings/fleets`}>
            {t("settings.fence.create_fleet_link")}
          </Link>
        </Card>
      ) : (
        <Accordion
          className="fleet-accordion"
          activeKey={expandedFleetUuid ?? undefined}
          onSelect={handleAccordionSelect}
          alwaysOpen={false}
        >
          {fleets.map((f) => (
            <FleetAccordionItem
              key={f.uuid}
              fleet={f}
              partnerUuid={partner.uuid}
              fencesState={fencesByFleet[f.uuid]}
              onDeleteFence={handleDeleteFence}
            />
          ))}
        </Accordion>
      )}
    </Page>
  )
}

export default SelectFleetScreen
